export const T12ExpenseAnalysis = () => {
  return (
    <section className="py-20 bg-gradient-to-br from-slate-50 to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-teal-50 border border-teal-200 rounded-full mb-6">
            <span className="text-teal-600 text-sm font-semibold">Expense Breakdown</span>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Trailing 12 Operating Expenses Deep Dive
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Operating expenses directly reduce your NOI and property value. Understanding what counts as an operating expense versus capital expenditure is critical for accurate T12 reporting.
          </p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-12 items-start mb-16">
          <div>
            <div className="aspect-w-4 aspect-h-3 rounded-2xl overflow-hidden shadow-2xl mb-6">
              <img 
                src="https://public.readdy.ai/ai/img_res/8e887ff28380e482317377e2d36e1178.jpg"
                alt="Operating expense breakdown infographic for apartment buildings"
                className="w-full h-full object-cover"
              />
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Typical Expense Ratios</h3>
              <p className="text-gray-700 mb-4">
                Operating expenses typically range from 35-50% of gross income, depending on property age, condition, and tenant responsibility for utilities.
              </p>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div>
                    <div className="font-semibold text-gray-900">35-40% Expense Ratio</div>
                    <div className="text-sm text-gray-600">Well-maintained, newer properties</div>
                  </div>
                  <div className="text-green-600 font-bold">Excellent</div>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div>
                    <div className="font-semibold text-gray-900">40-45% Expense Ratio</div>
                    <div className="text-sm text-gray-600">Average market conditions</div>
                  </div>
                  <div className="text-blue-600 font-bold">Normal</div>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div>
                    <div className="font-semibold text-gray-900">45-50% Expense Ratio</div>
                    <div className="text-sm text-gray-600">Older properties, high maintenance</div>
                  </div>
                  <div className="text-yellow-600 font-bold">Concerning</div>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div>
                    <div className="font-semibold text-gray-900">50%+ Expense Ratio</div>
                    <div className="text-sm text-gray-600">Major red flag - deferred maintenance</div>
                  </div>
                  <div className="text-red-600 font-bold">Problem</div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-government-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Property Taxes</h3>
                  <p className="text-gray-700 mb-3">
                    Usually the largest single expense, typically 10-15% of gross income. Property taxes are based on assessed value and local tax rates.
                  </p>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-sm text-gray-700 mb-2"><strong>Example Calculation:</strong></p>
                    <div className="space-y-1 text-sm text-gray-700">
                      <div className="flex justify-between">
                        <span>Assessed Value:</span>
                        <span className="font-semibold">$2,800,000</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Tax Rate:</span>
                        <span className="font-semibold">1.5%</span>
                      </div>
                      <div className="flex justify-between border-t border-gray-300 pt-1 mt-1">
                        <span className="font-bold">Annual Property Tax:</span>
                        <span className="font-bold text-teal-600">$42,000</span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-gray-700"><strong>Buyer Note:</strong> Buyers verify property tax amounts with county records. Underreporting taxes is a major red flag.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-check-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Insurance</h3>
                  <p className="text-gray-700 mb-3">
                    Property and liability insurance typically costs 4-8% of gross income. Rates vary by location, property age, claims history, and coverage levels.
                  </p>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-700">Property Insurance:</span>
                      <span className="font-semibold text-gray-900">$12,000-15,000/year</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-700">Liability Insurance:</span>
                      <span className="font-semibold text-gray-900">$3,000-5,000/year</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-700">Umbrella Policy:</span>
                      <span className="font-semibold text-gray-900">$1,000-2,000/year</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-water-flash-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Utilities</h3>
                  <p className="text-gray-700 mb-3">
                    Costs vary dramatically based on whether tenants or landlord pays utilities. Common area utilities are always landlord responsibility.
                  </p>
                  <div className="space-y-2">
                    <div className="bg-slate-50 rounded-lg p-3">
                      <div className="flex justify-between items-center text-sm mb-1">
                        <span className="text-gray-700 font-semibold">Water & Sewer:</span>
                        <span className="font-bold text-gray-900">$50-80/unit/month</span>
                      </div>
                      <p className="text-xs text-gray-600">Usually landlord-paid, can be 10-15% of gross income</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <div className="flex justify-between items-center text-sm mb-1">
                        <span className="text-gray-700 font-semibold">Trash Collection:</span>
                        <span className="font-bold text-gray-900">$20-30/unit/month</span>
                      </div>
                      <p className="text-xs text-gray-600">Typically landlord responsibility</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <div className="flex justify-between items-center text-sm mb-1">
                        <span className="text-gray-700 font-semibold">Common Area Electric:</span>
                        <span className="font-bold text-gray-900">$200-500/month</span>
                      </div>
                      <p className="text-xs text-gray-600">Hallways, exterior lighting, laundry rooms</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-tools-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Maintenance & Repairs</h3>
                  <p className="text-gray-700 mb-3">
                    Ongoing maintenance typically runs 8-12% of gross income. This is one of the most scrutinized expense categories.
                  </p>
                  <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded-r-lg p-4">
                    <p className="text-sm text-gray-700 mb-2"><strong>Red Flag Alert:</strong></p>
                    <p className="text-sm text-gray-700">
                      Unusually low maintenance expenses suggest deferred maintenance. Buyers will assume they'll need to spend significantly more post-purchase and adjust their offer accordingly.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-settings-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Property Management</h3>
                  <p className="text-gray-700 mb-3">
                    Professional management typically costs 8-10% of collected income. Even if you self-manage, buyers calculate this expense.
                  </p>
                  <div className="bg-teal-50 rounded-lg p-4">
                    <p className="text-sm text-gray-700 mb-2"><strong>Important:</strong></p>
                    <p className="text-sm text-gray-700">
                      If you self-manage and don't include management fees in your T12, sophisticated buyers will add this expense when calculating NOI, reducing your property's value by 8-10%.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200 mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Complete Operating Expense Categories</h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-lg p-4 border border-gray-200">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-building-line text-teal-600"></i>
                Property Operations
              </h4>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span>Property taxes</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span>Property insurance</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span>Property management fees</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span>Legal and professional fees</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span>Accounting and bookkeeping</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-lg p-4 border border-gray-200">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-water-flash-line text-teal-600"></i>
                Utilities
              </h4>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span>Water and sewer</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span>Trash collection</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span>Common area electricity</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span>Gas (if landlord-paid)</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span>Internet/cable (common areas)</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-lg p-4 border border-gray-200">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-tools-line text-teal-600"></i>
                Maintenance
              </h4>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span>Repairs and maintenance</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span>Landscaping and grounds</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span>Snow removal</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span>Pest control</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span>Janitorial services</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-lg p-4 border border-gray-200">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-team-line text-teal-600"></i>
                Leasing & Marketing
              </h4>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span>Advertising and marketing</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span>Leasing commissions</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span>Tenant screening costs</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span>Unit turnover costs</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-lg p-4 border border-gray-200">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-file-list-line text-teal-600"></i>
                Administrative
              </h4>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span>Office supplies</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span>Bank fees</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span>Software and technology</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span>Licenses and permits</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-lg p-4 border border-gray-200">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-shield-line text-teal-600"></i>
                Security & Safety
              </h4>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span>Security systems</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span>Fire safety equipment</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span>Elevator maintenance</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                  <span>Emergency repairs</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <div className="bg-red-50 border-l-4 border-red-500 rounded-r-lg p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-close-circle-line text-2xl text-red-600"></i>
            </div>
            <div>
              <h4 className="font-bold text-gray-900 mb-3">What NOT to Include as Operating Expenses</h4>
              <div className="grid md:grid-cols-2 gap-4">
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-close-line text-red-600 mt-1 font-bold"></i>
                    <span><strong>Mortgage payments</strong> - Debt service is not an operating expense</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-close-line text-red-600 mt-1 font-bold"></i>
                    <span><strong>Capital expenditures</strong> - New roof, HVAC replacement, major renovations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-close-line text-red-600 mt-1 font-bold"></i>
                    <span><strong>Depreciation</strong> - Tax concept, not actual cash expense</span>
                  </li>
                </ul>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-close-line text-red-600 mt-1 font-bold"></i>
                    <span><strong>Owner's personal expenses</strong> - Personal vehicle, home office, etc.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-close-line text-red-600 mt-1 font-bold"></i>
                    <span><strong>Income taxes</strong> - Personal tax liability</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-close-line text-red-600 mt-1 font-bold"></i>
                    <span><strong>Reserves</strong> - Future expense savings, not current expenses</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};